import { createTheme } from '@mui/material/styles';
import { THEME_COLORS, GRADIENTS } from '../utils/constants';

// Define theme colors using the professional color system
export const colors = {
  // Primary colors - Professional gold from brand palette
  primary: {
    main: THEME_COLORS.gold.primary,
    light: THEME_COLORS.gold.light,
    dark: THEME_COLORS.gold.dark,
    contrastText: THEME_COLORS.neutral.white,
  },
  // Secondary colors - Professional grays
  secondary: {
    main: THEME_COLORS.primary.gray,
    light: THEME_COLORS.neutral.lightGray,
    dark: THEME_COLORS.primary.dark,
    contrastText: THEME_COLORS.neutral.white,
  },
  // Accent color - Deep brown for sophisticated contrast
  accent: {
    main: THEME_COLORS.primary.dark,
    light: THEME_COLORS.neutral.charcoal,
    dark: THEME_COLORS.neutral.black,
    contrastText: THEME_COLORS.neutral.white,
  },
  // Professional semantic colors
  error: {
    main: THEME_COLORS.semantic.error,
    light: '#FFCDD2',
    dark: '#B71C1C',
  },
  warning: {
    main: THEME_COLORS.semantic.warning,
    light: '#FFE0B2',
    dark: '#E65100',
  },
  info: {
    main: THEME_COLORS.semantic.info,
    light: '#BBDEFB',
    dark: '#0D47A1',
  },
  success: {
    main: THEME_COLORS.semantic.success,
    light: '#C8E6C9',
    dark: '#1B5E20',
  },
  // Professional light theme backgrounds
  background: {
    default: THEME_COLORS.neutral.warmWhite,
    paper: THEME_COLORS.neutral.white,
    dark: THEME_COLORS.primary.dark,
    card: THEME_COLORS.neutral.white,
    gradient: GRADIENTS.lightPrimary,
  },
  text: {
    primary: THEME_COLORS.primary.dark,
    secondary: THEME_COLORS.primary.gray,
    disabled: THEME_COLORS.neutral.lightGray,
    accent: THEME_COLORS.gold.primary,
  },
  divider: 'rgba(45, 41, 38, 0.08)',
  custom: {
    lightCream: THEME_COLORS.neutral.lightCream,
    cream: THEME_COLORS.neutral.cream,
    warmWhite: THEME_COLORS.neutral.warmWhite,
    darkBrown: THEME_COLORS.primary.dark,
    charcoal: THEME_COLORS.neutral.charcoal,
    mediumGray: THEME_COLORS.primary.gray,
    primaryGold: THEME_COLORS.gold.primary,
    lightGold: THEME_COLORS.gold.light,
    darkGold: THEME_COLORS.gold.dark,
    goldGradient: GRADIENTS.primaryGold,
    goldAccent: THEME_COLORS.gold.primary,
    softGold: THEME_COLORS.gold.lightest,
    lightGradient: GRADIENTS.lightSubtle,
    heroGradient: GRADIENTS.heroBackground,
    cardGradient: 'linear-gradient(135deg, rgba(255, 255, 255, 0.95) 0%, rgba(251, 249, 246, 0.95) 100%)',
  }
};

// Create a responsive typography scale with more modern fonts
const typography = {
  fontFamily: '"Inter", "Plus Jakarta Sans", "Roboto", "Helvetica", "Arial", sans-serif',
  h1: {
    fontSize: '3rem',
    fontWeight: 800,
    lineHeight: 1.1,
    letterSpacing: '-0.02em',
  },
  h2: {
    fontSize: '2.25rem',
    fontWeight: 700,
    lineHeight: 1.2,
    letterSpacing: '-0.01em',
  },
  h3: {
    fontSize: '1.875rem',
    fontWeight: 700,
    lineHeight: 1.2,
    letterSpacing: '-0.005em',
  },
  h4: {
    fontSize: '1.5rem',
    fontWeight: 600,
    lineHeight: 1.2,
    letterSpacing: '-0.005em',
  },
  h5: {
    fontSize: '1.25rem',
    fontWeight: 600,
    lineHeight: 1.4,
    letterSpacing: '0em',
  },
  h6: {
    fontSize: '1rem',
    fontWeight: 600,
    lineHeight: 1.4,
    letterSpacing: '0.0075em',
  },
  subtitle1: {
    fontSize: '1rem',
    fontWeight: 500,
    lineHeight: 1.5,
    letterSpacing: '0.00938em',
  },
  subtitle2: {
    fontSize: '0.875rem',
    fontWeight: 500,
    lineHeight: 1.5,
    letterSpacing: '0.00714em',
  },
  body1: {
    fontSize: '1rem',
    fontWeight: 400,
    lineHeight: 1.5,
    letterSpacing: '0.00938em',
  },
  body2: {
    fontSize: '0.875rem',
    fontWeight: 400,
    lineHeight: 1.5,
    letterSpacing: '0.01071em',
  },
  button: {
    fontSize: '0.875rem',
    fontWeight: 600,
    lineHeight: 1.75,
    letterSpacing: '0.02857em',
    textTransform: 'none',
  },
  caption: {
    fontSize: '0.75rem',
    fontWeight: 400,
    lineHeight: 1.66,
    letterSpacing: '0.03333em',
  },
  overline: {
    fontSize: '0.625rem',
    fontWeight: 500,
    lineHeight: 2.66,
    letterSpacing: '0.08333em',
    textTransform: 'uppercase',
  },
};

// Define component overrides for a sleek, modern design
const components = {
  MuiButton: {
    styleOverrides: {
      root: {
        borderRadius: 8,
        padding: '10px 22px',
        boxShadow: 'none',
        textTransform: 'none',
        fontWeight: 600,
        transition: 'all 0.2s ease-in-out',
        '&:hover': {
          transform: 'translateY(-2px)',
          boxShadow: '0px 4px 12px rgba(0, 0, 0, 0.35)',
        },
      },
      contained: {
        '&:hover': {
          boxShadow: '0px 6px 16px rgba(0, 0, 0, 0.4)',
        },
      },
      containedPrimary: {
        background: GRADIENTS.goldPrimary,
        color: THEME_COLORS.neutral.black,
      },
      containedSecondary: {
        background: GRADIENTS.darkPrimary,
      },
      outlined: {
        borderWidth: '1.5px',
        '&:hover': {
          borderWidth: '1.5px',
        },
      },
      outlinedPrimary: {
        borderColor: colors.primary.main,
        '&:hover': {
          borderColor: colors.primary.light,
          backgroundColor: 'rgba(212, 175, 55, 0.08)',
        }
      }
    },
  },
  MuiCard: {
    styleOverrides: {
      root: {
        borderRadius: 12,
        boxShadow: '0px 8px 24px rgba(0, 0, 0, 0.15)',
        overflow: 'hidden',
        transition: 'transform 0.3s ease, box-shadow 0.3s ease',
        background: colors.background.paper,
        border: `1px solid ${colors.divider}`,
        '&:hover': {
          transform: 'translateY(-4px)',
          boxShadow: '0px 12px 28px rgba(0, 0, 0, 0.25)',
        },
      },
    },
  },
  MuiCardHeader: {
    styleOverrides: {
      root: {
        padding: '20px 20px 8px 20px',
      },
      title: {
        fontWeight: 600,
        color: colors.text.primary,
      },
      subheader: {
        color: colors.text.secondary,
      }
    },
  },
  MuiCardContent: {
    styleOverrides: {
      root: {
        padding: '20px',
        '&:last-child': {
          paddingBottom: '20px',
        },
      },
    },
  },
  MuiPaper: {
    styleOverrides: {
      rounded: {
        borderRadius: 12,
      },
      elevation1: {
        boxShadow: '0px 4px 16px rgba(0, 0, 0, 0.15)',
      },
      elevation2: {
        boxShadow: '0px 8px 20px rgba(0, 0, 0, 0.2)',
      },
      elevation3: {
        boxShadow: '0px 12px 24px rgba(0, 0, 0, 0.25)',
      },
    },
  },
  MuiTextField: {
    styleOverrides: {
      root: {
        '& .MuiOutlinedInput-root': {
          borderRadius: 8,
          transition: 'box-shadow 0.2s ease-in-out',
          '& fieldset': {
            borderColor: 'rgba(255, 255, 255, 0.15)',
          },
          '&:hover fieldset': {
            borderColor: 'rgba(255, 255, 255, 0.25)',
          },
          '&.Mui-focused fieldset': {
            borderColor: colors.primary.main,
          },
          '&:hover': {
            boxShadow: '0px 4px 10px rgba(0, 0, 0, 0.2)',
          },
          '&.Mui-focused': {
            boxShadow: `0px 6px 15px rgba(212, 175, 55, 0.25)`,
          },
        },
      },
    },
  },
  MuiAppBar: {
    styleOverrides: {
      root: {
        boxShadow: '0px 2px 15px rgba(0, 0, 0, 0.15)',
        backgroundImage: GRADIENTS.darkPrimary,
        borderBottom: `1px solid ${THEME_COLORS.gold.primary}30`,
      },
    },
  },
  MuiChip: {
    styleOverrides: {
      root: {
        borderRadius: 6,
        fontWeight: 500,
        transition: 'all 0.2s ease',
        '&:hover': {
          boxShadow: '0px 2px 6px rgba(0, 0, 0, 0.25)',
        },
      },
      colorPrimary: {
        background: GRADIENTS.goldAccent,
        color: THEME_COLORS.neutral.black,
      },
      colorSecondary: {
        background: GRADIENTS.darkPrimary,
      },
    },
  },
  MuiTab: {
    styleOverrides: {
      root: {
        fontWeight: 600,
        textTransform: 'none',
        minWidth: 120,
        '&.Mui-selected': {
          color: colors.accent.light,
        },
      },
    },
  },
  MuiTabs: {
    styleOverrides: {
      indicator: {
        height: 3,
        borderRadius: 3,
        backgroundImage: GRADIENTS.goldAccent,
      },
    },
  },
  MuiAlert: {
    styleOverrides: {
      root: {
        borderRadius: 12,
        boxShadow: '0px 4px 15px rgba(0, 0, 0, 0.05)',
      },
      standardSuccess: {
        backgroundImage: 'linear-gradient(135deg, rgba(76, 175, 80, 0.1) 0%, rgba(129, 199, 132, 0.1) 100%)',
        borderLeft: '4px solid #4CAF50',
      },
      standardError: {
        backgroundImage: 'linear-gradient(135deg, rgba(244, 67, 54, 0.1) 0%, rgba(255, 82, 82, 0.1) 100%)',
        borderLeft: '4px solid #F44336',
      },
      standardWarning: {
        backgroundImage: 'linear-gradient(135deg, rgba(255, 152, 0, 0.1) 0%, rgba(255, 183, 77, 0.1) 100%)',
        borderLeft: '4px solid #FF9800',
      },
      standardInfo: {
        backgroundImage: 'linear-gradient(135deg, rgba(212, 175, 55, 0.1) 0%, rgba(255, 215, 0, 0.1) 100%)',
        borderLeft: '4px solid #D4AF37',
      },
    },
  },
};

// Create and export the theme
const theme = createTheme({
  palette: colors,
  typography,
  components,
  shape: {
    borderRadius: 12,
  },
  shadows: [
    'none',
    '0px 2px 8px rgba(0, 0, 0, 0.03)',
    '0px 4px 12px rgba(0, 0, 0, 0.04)',
    '0px 8px 16px rgba(0, 0, 0, 0.05)',
    '0px 12px 24px rgba(0, 0, 0, 0.05)',
    '0px 16px 32px rgba(0, 0, 0, 0.06)',
    '0px 20px 40px rgba(0, 0, 0, 0.07)',
    '0px 24px 48px rgba(0, 0, 0, 0.08)',
    '0px 28px 56px rgba(0, 0, 0, 0.09)',
    '0px 32px 64px rgba(0, 0, 0, 0.1)',
    '0px 36px 72px rgba(0, 0, 0, 0.11)',
    '0px 40px 80px rgba(0, 0, 0, 0.12)',
    '0px 44px 88px rgba(0, 0, 0, 0.13)',
    '0px 48px 96px rgba(0, 0, 0, 0.14)',
    '0px 52px 104px rgba(0, 0, 0, 0.15)',
    '0px 56px 112px rgba(0, 0, 0, 0.16)',
    '0px 60px 120px rgba(0, 0, 0, 0.17)',
    '0px 64px 128px rgba(0, 0, 0, 0.18)',
    '0px 68px 136px rgba(0, 0, 0, 0.19)',
    '0px 72px 144px rgba(0, 0, 0, 0.2)',
    '0px 76px 152px rgba(0, 0, 0, 0.21)',
    '0px 80px 160px rgba(0, 0, 0, 0.22)',
    '0px 84px 168px rgba(0, 0, 0, 0.23)',
    '0px 88px 176px rgba(0, 0, 0, 0.24)',
    '0px 92px 184px rgba(0, 0, 0, 0.25)',
  ],
});

export default theme;
